import { useMarketingTracking } from "../../marketing.analytics";
import { useNetworkSnapshot } from "../../hooks/useNetworkSnapshot";
import {
  useFetchFeaturedOrganizations,
  useFetchPublicOrganizations,
} from "@core/features/organization";
import { marketingContentData } from "./marketingContent.data";
import {
  marketingPartnerOrganizationsData,
} from "./marketingPartnerOrganizations.data";
import {
  networkCounts as countPlaceholders,
  networkSnapshot as snapshotPlaceholders,
} from "./marketingFund.data";
import { MarketingContent } from "./MarketingContent";

export type Props = {
  /** The News section, passed through to its place on the page */
  news?: React.ReactNode;
};

export function MarketingContentContainer({ news }: Props = {}) {
  const { trackCtaClick } = useMarketingTracking();
  const { data: snapshot, isLoading: isFiguresLoading } = useNetworkSnapshot();
  const { data: featured, isLoading: isPartnersLoading } = useFetchFeaturedOrganizations();
  const { data: listed } = useFetchPublicOrganizations("all");

  const handleDonateClick = () => {
    trackCtaClick("donate");
    window.open(
      "https://www.every.org/outlast-foundation/f/pollinator",
      "_blank"
    );
  };

  // Each figure the backend can count replaces its placeholder; the ones it
  // cannot stay visibly blank rather than invented.
  const count = (n: number | null | undefined, fallback: string) =>
    n == null ? fallback : n.toLocaleString("en-US");
  const stats = snapshot
    ? [
        {
          value:
            snapshot.facilitator_share == null
              ? snapshotPlaceholders[0].value
              : `${snapshot.facilitator_share}%`,
          label: snapshotPlaceholders[0].label,
        },
        { value: count(snapshot.collective_members, "—"), label: snapshotPlaceholders[1].label },
        { value: count(snapshot.researchers_faculty, "—"), label: snapshotPlaceholders[2].label },
        { value: count(snapshot.fulbright_fellows, "—"), label: snapshotPlaceholders[3].label },
        { value: count(snapshot.guggenheim_fellows, "—"), label: snapshotPlaceholders[4].label },
        { value: count(snapshot.founders_org_leaders, "—"), label: snapshotPlaceholders[5].label },
        { value: count(snapshot.countries, "—"), label: snapshotPlaceholders[6].label },
        { value: count(snapshot.invited_by_peer, "—"), label: snapshotPlaceholders[7].label },
        { value: count(snapshot.peer_groups, "—"), label: snapshotPlaceholders[8].label },
      ]
    : snapshotPlaceholders;
  const counts = snapshot
    ? {
        participants: count(snapshot.participants, countPlaceholders.participants),
        organizations: count(snapshot.organizations, countPlaceholders.organizations),
        sharingShare:
          snapshot.grantmaker_sharing_share == null
            ? countPlaceholders.sharingShare
            : `${snapshot.grantmaker_sharing_share}%`,
      }
    : countPlaceholders;
  // The page previews every organization staff have marked as featured, so
  // what appears here is theirs to curate by featuring and unfeaturing.
  // The panel behind "here" lists every organization in the network. The
  // design's transcribed list stands in for either if it cannot be fetched or
  // is empty.
  const featuredOrDrawn = featured?.length ? featured : marketingPartnerOrganizationsData;
  const preview = featuredOrDrawn;
  const partners = listed?.length ? listed : featuredOrDrawn;

  return (
    <MarketingContent
      sectionsContent={marketingContentData}
      networkSnapshot={stats}
      networkCounts={counts}
      partnerPreview={preview}
      partnerOrganizations={partners}
      isPartnersLoading={isPartnersLoading}
      isFiguresLoading={isFiguresLoading}
      news={news}
      onDonateClick={handleDonateClick}
      // Named as the hero card's button to the same form is, so the two are
      // one call to action in the funnel wherever on the page it was pressed.
      onScheduleMeetingClick={() =>
        trackCtaClick("Request a Meeting", { form_slug: "partner_with_us" })
      }
    />
  );
}

export default MarketingContentContainer;
